import { useCallback, useRef, useState } from 'react';
import ApprovalMfaModal from '../components/security/ApprovalMfaModal';
import endpoints from '../api/endpoints';
import { parseApiError } from '../utils/notify';

// Approval MFA — orchestrates the full step-up flow for any protected
// operation. SEPARATE from the existing `useMfaAction` hook (which is
// designed for one-shot per-action TOTP that gets sent INLINE with
// the protected route's body). Approval MFA is a multi-step server-
// side state machine — we create a challenge, verify L1, optionally
// verify L2, run the protected mutation, then mark the challenge
// consumed.
//
// Usage:
//
//   const { runWithApproval, ApprovalMfaModalSlot } = useApprovalMfa();
//
//   async function handleDelete(record) {
//     try {
//       await runWithApproval({
//         operationKey: 'demo.customer.delete',
//         resourceType: 'demo_customer',
//         resourceId:   record.id,
//         operationLabel: 'Delete customer',
//         resourceLabel:  `${record.code} · ${record.name}`,
//         action: (challengeId) =>
//           endpoints.approvalMfa.demoCustomerDelete(record.id, {
//             approvalMfaChallengeId: challengeId
//           })
//       });
//       notifySuccess('Customer deleted');
//     } catch (err) {
//       if (err && err.name === 'ApprovalMfaCancelled') return;
//       notifyError('Could not delete customer', parseApiError(err)?.description);
//     }
//   }
//
//   return (
//     <>
//       <Button onClick={() => handleDelete(record)}>Delete</Button>
//       {ApprovalMfaModalSlot}
//     </>
//   );
//
// State machine:
//   idle → creating → l1 → (verifying L1)
//                    ↓ success
//                   l1-done
//                    ↓ (if L1 only) → running → consumed → resolve
//                    ↓ (if L1+L2)
//                   l2 → (verifying L2)
//                    ↓ success
//                   l2-done → running → consumed → resolve

const APPROVAL_MFA_RETRYABLE_CODES = new Set([
  'MFA_INVALID_CODE',
  'APPROVAL_MFA_CODE_REUSED'
]);

const APPROVAL_MFA_TERMINAL_CODES = new Set([
  'MFA_TOO_MANY_ATTEMPTS',
  'MFA_NOT_CONFIGURED',
  'MFA_REQUIRED',           // tenant MFA isn't set up
  'APPROVAL_MFA_FAILED',
  'APPROVAL_MFA_EXPIRED',
  'APPROVAL_MFA_NOT_FOUND',
  'APPROVAL_MFA_OPERATION_MISMATCH',
  'APPROVAL_MFA_RESOURCE_MISMATCH',
  'APPROVAL_MFA_ALREADY_CONSUMED'
]);

export default function useApprovalMfa() {
  // Pending action lives in a ref so the modal's verify callback
  // always sees the latest pending state without recreating itself
  // on every render.
  const pendingRef = useRef(null);

  const [open, setOpen]           = useState(false);
  const [step, setStep]           = useState('l1');           // 'l1' | 'l2'
  const [requiresL2, setRequiresL2] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [operationLabel, setOperationLabel] = useState(null);
  const [resourceLabel, setResourceLabel]   = useState(null);

  function reset() {
    pendingRef.current = null;
    setOpen(false);
    setStep('l1');
    setRequiresL2(false);
    setLoading(false);
    setErrorText(null);
    setOperationLabel(null);
    setResourceLabel(null);
  }

  // Run a protected action through the Approval MFA flow.
  const runWithApproval = useCallback((opts = {}) => {
    return new Promise((resolve, reject) => {
      if (pendingRef.current) {
        try {
          pendingRef.current.reject(
            Object.assign(new Error('superseded'), { name: 'ApprovalMfaCancelled' })
          );
        } catch (_e) { /* noop */ }
      }

      pendingRef.current = {
        resolve,
        reject,
        opts,
        challengeId: null  // populated after createChallenge
      };

      setOperationLabel(opts.operationLabel || opts.operationKey || null);
      setResourceLabel(opts.resourceLabel || null);
      setStep('l1');
      setErrorText(null);
      setLoading(true);
      setOpen(true);

      // Kick off challenge creation. Once we know the required level
      // from the server, we can show the right step indicator.
      endpoints.approvalMfa
        .createChallenge({
          operationKey: opts.operationKey,
          resourceType: opts.resourceType,
          resourceId:   opts.resourceId,
          metadata:     opts.metadata
        })
        .then((challenge) => {
          if (!pendingRef.current) return;
          pendingRef.current.challengeId  = challenge.challengeId;
          pendingRef.current.requiredLevel = challenge.requiredLevel;
          setRequiresL2(challenge.requiredLevel === 'l1_and_l2_approval');
          setLoading(false);
          setErrorText(null);
        })
        .catch((err) => {
          const pending = pendingRef.current;
          reset();
          if (pending && typeof pending.reject === 'function') {
            pending.reject(err);
          }
        });
    });
  }, []);

  // Called by the modal when user submits a 6-digit code.
  const handleVerify = useCallback(async (code) => {
    const pending = pendingRef.current;
    if (!pending || !pending.challengeId) return;

    setLoading(true);
    setErrorText(null);

    try {
      if (step === 'l1') {
        await endpoints.approvalMfa.verifyL1({
          challengeId: pending.challengeId,
          mfaCode:     code
        });

        if (pending.requiredLevel === 'l1_and_l2_approval') {
          // Move to L2 — reset busy and step state.
          setStep('l2');
          setLoading(false);
          setErrorText(null);
          return;
        }
        // L1-only — run the protected action.
        await runProtectedAction(pending);
        return;
      }

      if (step === 'l2') {
        await endpoints.approvalMfa.verifyL2({
          challengeId: pending.challengeId,
          mfaCode:     code
        });
        await runProtectedAction(pending);
        return;
      }
    } catch (err) {
      const parsed = parseApiError(err);
      const code1  = parsed && parsed.code;

      if (APPROVAL_MFA_RETRYABLE_CODES.has(code1)) {
        // Stay in the modal so the user can retype the next code.
        setLoading(false);
        setErrorText(parsed.description || 'Invalid verification code.');
        return;
      }

      if (APPROVAL_MFA_TERMINAL_CODES.has(code1)) {
        const pendingNow = pendingRef.current;
        reset();
        if (pendingNow && typeof pendingNow.reject === 'function') {
          pendingNow.reject(err);
        }
        return;
      }

      // Any other error from the protected action itself — bubble up.
      const pendingNow = pendingRef.current;
      reset();
      if (pendingNow && typeof pendingNow.reject === 'function') {
        pendingNow.reject(err);
      }
    }
  }, [step]);

  async function runProtectedAction(pending) {
    setLoading(true);
    setErrorText(null);
    try {
      const result = await pending.opts.action(pending.challengeId);
      pending.resolve(result);
      reset();
    } catch (err) {
      // The protected action itself failed (e.g. validation error
      // outside the approval-MFA gate). Propagate without retry.
      reset();
      if (typeof pending.reject === 'function') pending.reject(err);
    }
  }

  const handleCancel = useCallback(() => {
    const pending = pendingRef.current;
    reset();
    if (pending && typeof pending.reject === 'function') {
      pending.reject(
        Object.assign(new Error('approval mfa cancelled'), {
          name: 'ApprovalMfaCancelled'
        })
      );
    }
  }, []);

  const ApprovalMfaModalSlot = (
    <ApprovalMfaModal
      open={open}
      step={step}
      requiresL2={requiresL2}
      operationLabel={operationLabel}
      resourceLabel={resourceLabel}
      loading={loading}
      errorText={errorText}
      onVerify={handleVerify}
      onCancel={handleCancel}
    />
  );

  return { runWithApproval, ApprovalMfaModalSlot };
}
